import React from 'react'
import { Translator, Countries, Languages } from '@merchant-portal/mp-models'
import {
  getLocalizedCustomerSupport,
  getLocalizedCreditSupport,
  getLocalizedChatSupport,
} from '../../models/contactInformation'
import {
  getCountryFromLanguage,
  getCurrencySymbol,
  getEIDProviderName,
  isEIDLinkRequired,
} from '../../models/countries'

const swedish = require('../../assets/i18n/sv.json')
const swedishPhrase = require('../../assets/i18n/sv_phrase.json')
const norwegian = require('../../assets/i18n/nb.json')
const norwegianPhrase = require('../../assets/i18n/nb_phrase.json')
const danish = require('../../assets/i18n/da.json')
const danishPhrase = require('../../assets/i18n/da_phrase.json')
const finnish = require('../../assets/i18n/fi.json')
const finnishPhrase = require('../../assets/i18n/fi_phrase.json')

export const translator = new Translator(swedish, norwegian, danish, finnish)

export const phraseTranslator = new Translator(
  swedishPhrase,
  norwegianPhrase,
  danishPhrase,
  finnishPhrase,
)

const getLocale = () => {
  const localeString = localStorage.getItem('locale')
  return (localeString && JSON.parse(localeString)) || {}
}

const getTranslation = (label, args) => {
  const { language } = getLocale()
  if (language && language !== translator.currentLanguage) {
    translator.setLanguage(language)
  }
  if (phraseTranslator.currentLanguage !== translator.currentLanguage) {
    phraseTranslator.setLanguage(language)
  }
  const result = phraseTranslator.getText(label) || translator.getText(label)
  // eslint-disable-next-line no-use-before-define
  return result ? replaceArgs(result, args) : ''
}

export const getText = (label, arg, ...args) => {
  if (
    arg != null &&
    Object.prototype.toString.call(arg) === '[object Object]'
  ) {
    return getTranslation(label, arg)
  }
  return getTranslation(label, arg != null ? [arg, ...args] : [])
}

export const getCountry = () => {
  const { country } = getLocale()
  return country || Countries.SE
}

export const getLanguage = () => {
  const { language } = getLocale()
  return language || Languages.SWEDISH
}

const templateFunctions = {
  bold: (language, value) => <b>{value}</b>,
  currency: (language, value) => {
    const countryCode = getCountry()
    return (
      (value && getCurrencySymbol(countryCode).wrap(`{${value}}`)) ||
      getCurrencySymbol(countryCode).toString()
    )
  },
  creditSupportPhoneNumber: () =>
    getLocalizedCreditSupport(getCountry()).phoneNumber,
  creditSupportOpeningHours: () =>
    getLocalizedCreditSupport(getCountry()).openingHours.join(', '),
  customerSupportPhoneNumber: () =>
    getLocalizedCustomerSupport(getCountry()).phoneNumber,
  customerSupportOpeningHours: () =>
    getLocalizedCustomerSupport(getCountry()).openingHours.join(', '),
  chatSupportOpeningHours: () =>
    getLocalizedChatSupport(getCountry()).openingHours.join(', '),
  eIdProvider: getEIDProviderName,
  eSignDescription: countryCode =>
    isEIDLinkRequired(countryCode)
      ? getText('payApply.signing.descrLink')
      : getText('payApply.signing.descrDirect'),
  eIdDescription: countryCode =>
    isEIDLinkRequired(countryCode)
      ? getText('payApply.identifying.descrLink')
      : getText('payApply.identifying.descrDirect'),
}

/* eslint-disable no-console */
const getTemplateFunction = name =>
  templateFunctions[name] ||
  (!console.log('template function not found:', name) &&
    (value => `{${value}}`))

const replaceArgs = (text, args) => {
  let res = text
  const templateFunctionRegex = /{#(\w+)\((.*?)\)}/g
  let rex
  const substitutions = {}
  let id = 0
  // eslint-disable-next-line no-cond-assign
  while ((rex = templateFunctionRegex.exec(text))) {
    const [match, func, argument] = rex
    const val = getTemplateFunction(func)(
      getCountryFromLanguage(translator.currentLanguage),
      argument,
    )
    if (typeof val === 'object') {
      substitutions[id] = val
      res = res.replace(match, `$sub_${id}_`)
      id += 1
    } else {
      res = res.replace(
        match,
        getTemplateFunction(func)(
          getCountryFromLanguage(translator.currentLanguage),
          ...argument.split(',').map(v => v.trim()),
        ),
      )
    }
  }
  if (id > 0) {
    return (
      <>
        {res
          .split(/\$sub_(.*?)_/g)
          .map(s => substitutions[s] || s)
          .map((v, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={v + i}>{v}</React.Fragment>
          ))}
      </>
    )
  }
  const templateWithoutFunctions = res
  const templateValueRegex = /{(.*?)}/g

  let valueRex
  // eslint-disable-next-line no-cond-assign
  while ((valueRex = templateValueRegex.exec(templateWithoutFunctions))) {
    const [match, key] = valueRex
    res = res.replace(match, args[key] || '')
  }
  return res
}

export class CountryLabels {
  constructor() {
    this.labels = {}
    this.labels[Countries.SE] = getText('countries.sweden')
    this.labels[Countries.DK] = getText('countries.denmark')
    this.labels[Countries.NO] = getText('countries.norway')
    this.labels[Countries.FI] = getText('countries.finland')
  }
}

export const getCountryNames = () => {
  const countryNames = []
  const countryLabels = new CountryLabels()
  Object.keys(Countries).forEach(countryId => {
    countryNames.push(countryLabels.labels[countryId])
    return null
  })
  return countryNames
}

export const getCountryName = id => {
  const countryIds = Object.keys(Countries)
  const countryLabels = new CountryLabels()
  for (let i = 0; i < countryIds.length; i += 1) {
    if (countryIds[i] === id) {
      return countryLabels.labels[countryIds[i]]
    }
  }
  return null
}
